import { Tenor } from './roll-dates.model';
import { Date, DateSummary } from './date.model';
import { Benchmark, RateSummary } from '../models/rates.model';

export interface Pool {
  date: DateSummary;
  pool: PoolSummary;
  rate: RateSummary;
  entity: EntitySummary;
  orders: PoolOmsOrders[];
  direction: 'LONG' | 'SHORT';
  allocatedTotal: number;
  nearLegAllocatedTotal: number;
  farLegAllocatedTotal: number;
  remainingCredit: number;
  instrumentType: 'EVEN_SWAP' | 'UNEVEN_SWAP';
  nettingTotal: number;
  nettingPercentage: number;
  createdAt: string;
  updatedAt: string;
  updatedByName: string;
  orderAllocated: boolean;
  poolPreference: PoolPreference;
  chargedPoolSpread: number;
  status: PoolStatus;
  dealtSide: string;
  dealtCurrency: string;
  memberResidualSide: string;
  poolId: number;
  allocationsMatched?: boolean;
  edit?: boolean;
  allocatedAmount?: number;
  amount?: number;
  poolHierarchy?: string[];
  hasAllocatedTotal: number;
  matchTotal: number;
  matchPercentage: number;
}

export enum PoolStatus {
  open = 'OPEN',
  countdown = 'COUNTDOWN_TO_LOCK',
  locked = 'LOCKED',
  executed = 'EXECUTED',
}
export interface PoolStatusNotification {
  id: string;
  dateId: string;
  poolId: string;
  status: PoolStatus;
  countdownToLock: string;
  lockDate: string;
  rollDate: string;
  valueDate1: string;
  valueDate2: string;
}

export interface PoolSummary {
  id: string;
  date: DateSummary;
  legacyPoolId: number;
  currencyPair: string;
  tenor: Tenor;
}

export enum PoolPreference {
  base,
  quote,
}
export enum PoolPreferenceString {
  base = 'base',
  quote = 'quote',
}

export enum RollingPeriod {
  mm = 'mm',
  me = '1m',
  mmThreeMonths = '3m',
}

export type PoolCoverageCache = Record<string, number>;

export type PoolMap = Record<string, Pool>;

export type PoolPreferenceMap = Record<string, PoolPreference>;

export interface PoolRateAndSide {
  rate: number;
  side: string;
}

export interface RemainingCreditPayload {
  poolId: number;
  remainingCredit: number;
}

export interface PoolMinimum {
  id: string;
  minimum: number;
  active: boolean;
}

export interface EntityPreference {
  id: string;
  entity: EntitySummary;
  pool: PoolSummary;
  minimum: number;
  active: boolean;
  dealtPreference: string;
  upcomingPoolStatus: string;
  //TODO: these properties don't exist anymore, review and delete
  poolPreference?: PoolPreference;
  poolId?: string;
}

export interface EntitySummary {
  id: string;
  name: string;
  entityType: string;
}

export interface EntityPreferenceUpdatePayload {
  id: string;
  dealtPreference: string;
}

export interface PoolPreferenceUpdatePayload {
  currencyPair: string;
  tenor: string;
  entityId: string;
  dealtPreference: string;
}
export interface PoolStatusBannerInfo {
  displayedStatus: string;
  icon: string;
  class: string;
  message: string;
  desc: string;
  descClass?: string;
}

export interface RollingPeriodList {
  tenor: RollingPeriod;
  label: string;
}
export interface PoolReport {
  poolReportHierarchy: any;
  poolId: number;
  currencyPair: string;
  direction: string;
  dealtCurrency: string;
  rollingPeriod: string;
  memberId: number;
  memberName: string;
  estimatedMatch: number;
  estimatedResidual: number;
}

export interface PoolPositionsReport {
  poolReportHierarchy: any;
  date: DateSummary;
  pool: PoolSummary;
  entity: EntitySummary;
  direction: string;
  dealtCurrency: string;
  estimatedMatch: number;
  estimatedResidual: number;
  usdEstimatedMatch: number;
  usdEstimatedResidual: number;
}

export interface PoolOmsOrders {
  creditBankName: string;
  direction: string;
  dealtCurrency: string;
  dealtAmount: number;
  callRecords: PoolOmsOrdersDetails[];
  valueDate: string;
  orderAllocated: boolean;
  allocationsMatched: boolean;
  positionId: string;
  instrumentType: string;
  remainingCredit: number;
  dateEditable: boolean;
}
export interface PoolOmsOrdersDetails {
  orderId: string;
  accountName: string;
  direction: string;
  dealtCurrency: string;
  dealtAmount: number;
  orderGroupId: string;
  valueDate: string;
}

export interface CalendarPoolResponse {
  id: string;
  date: Date;
  status: 'OPENING SOON' | 'OPEN' | 'LOCKED' | 'MATCHED' | 'CLOSED';
  currencyPair: string;
  tenor: Tenor;
  benchmark: Benchmark;
  hasMatches: boolean;
  countDownToLockEnabled: boolean;
  lockDateEnabled: boolean;
  rollDateEnabled: boolean;
  settleDateEnabled: boolean;
  spotDateEnabled: boolean;
  valueDate1Enabled: boolean;
  valueDate2Enabled: boolean;
  straight1MonthDateEnabled: boolean;
  //TODO: define a separate viewModel, this field is not a part of the api response
  deletePool?: boolean;
}
export enum PoolWarningMessage {
  warningMessage = 'Please correct amount mismatch between Notional Position and Allocations.',
}
